/**
 * ActionDetailDialog — the rich, full-screen-overlay dialog that opens
 * when the user clicks an action row anywhere in the app.
 *
 * Modes
 * -----
 * - ``mode="active"`` (default): the action is live. Footer shows
 *   Accept / Dismiss buttons + an optional View Store / View Allocation
 *   navigation. Caller wires the Accept/Dismiss callbacks (so the page
 *   can track analytics + update its local ack state).
 *
 * - ``mode="done"``: the action has been marked-done or dismissed. The
 *   header shows an Outcome badge; the Financial Impact section gains an
 *   Actual P&L input (save on blur); the footer shows the marked-done-by
 *   info and a Reopen button. Mutations (set actual value, reopen,
 *   add comment) are wired inside the component via the Done hooks so
 *   the caller stays terse.
 *
 * Both modes share the same body: weather forecast, action playbook,
 * justification, financial impact, signal data, AI manager briefing, and
 * activity timeline. This is the part the user asked to be visually
 * consistent across /actions and /actions/done.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  AlertTriangle, RefreshCw, Bot, Zap, X, CheckCircle2, XCircle,
  ExternalLink, DollarSign, Tag, Layers, Activity, Info, Clock,
  RotateCcw, MessageSquare, User as UserIcon,
} from 'lucide-react'

import { trackEvent } from '../../analytics/mixpanel.js'
import client from '../../api/client.js'
import { useNow } from '../../hooks/useNow.js'
import {
  useMarkUndone, useSetActualValue, useAddComment,
} from '../../hooks/useDoneActions.js'
import { CATEGORY_META } from '../../utils/actionVisuals.js'
import { ACTION_TIPS } from '../../utils/actionTips.js'
import { leadTimeHours, formatLeadTime } from '../../utils/leadTime.js'
import { formatTempF, formatPrecipIn, formatSnowIn } from '../../utils/units.js'
import { PillarBadge, UrgencyBadge } from './ActionBadges.jsx'
import ActionHistoryTimeline from './ActionHistoryTimeline.jsx'


// ── Helpers + constants ──────────────────────────────────────────────────────

const SEV_COLORS = {
  0: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', bar: '#10b981' },
  1: { bg: 'bg-slate-500/10',   border: 'border-slate-500/20',   text: 'text-slate-400',   bar: '#64748b' },
  2: { bg: 'bg-yellow-500/10',  border: 'border-yellow-500/20',  text: 'text-yellow-400',  bar: '#eab308' },
  3: { bg: 'bg-orange-500/10',  border: 'border-orange-500/20',  text: 'text-orange-400',  bar: '#f97316' },
  4: { bg: 'bg-red-500/10',     border: 'border-red-500/20',     text: 'text-red-400',     bar: '#ef4444' },
  5: { bg: 'bg-red-700/20',     border: 'border-red-600/40',     text: 'text-red-300',     bar: '#dc2626' },
}

const WEATHER_EMOJI = {
  Sunny: '☀️', Cloudy: '☁️', Windy: '💨', Foggy: '🌫️',
  Drizzle: '🌦️', Rainy: '🌧️', Snowy: '❄️', Stormy: '⛈️',
}

const OUTCOME_BADGE = {
  marked_done:      { label: 'Accepted',  icon: CheckCircle2, color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30' },
  marked_dismissed: { label: 'Dismissed', icon: XCircle,      color: 'text-slate-300',   bg: 'bg-white/5',        border: 'border-white/10' },
}

function fmt(v) {
  if (!v || v === 0) return '—'
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`
  return `$${v.toFixed(0)}`
}

function fmtRelativeTs(iso) {
  if (!iso) return ''
  const ms = new Date(iso).getTime()
  const diff = Date.now() - ms
  const min = Math.round(diff / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  return `${day}d ago`
}


// ── Section header ───────────────────────────────────────────────────────────

function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-5 h-5 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0">
        <Icon size={11} className="text-slate-400" />
      </div>
      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{children}</span>
      <div className="flex-1 h-px bg-white/5" />
    </div>
  )
}


// ── 7-day weather strip ──────────────────────────────────────────────────────

function WeatherStrip({ dcId, triggerDate }) {
  const [forecast, setForecast] = useState(null)

  useEffect(() => {
    if (!dcId) return
    client.get(`/dc/${dcId}/forecast`)
      .then(({ data }) => setForecast(data.forecast))
      .catch(() => setForecast([]))
  }, [dcId])

  if (!forecast) {
    return (
      <div className="flex gap-2 overflow-x-auto px-0.5 py-1">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="flex-shrink-0 flex flex-col items-center gap-1.5 rounded-xl px-3 py-3 border border-white/5 bg-white/[0.025] animate-pulse"
            style={{ minWidth: 72, height: 140 }}
          >
            <div className="h-2.5 w-10 rounded bg-white/10" />
            <div className="h-6 w-6 rounded bg-white/10" />
            <div className="h-2.5 w-6 rounded bg-white/10" />
            <div className="h-2 w-8 rounded bg-white/10" />
            <div className="mt-auto w-full h-1 rounded-full bg-white/10" />
          </div>
        ))}
      </div>
    )
  }
  if (forecast.length === 0) {
    return <p className="text-xs text-slate-500 py-2">Forecast unavailable for this DC.</p>
  }

  return (
    <div className="flex gap-2 overflow-x-auto px-0.5 py-1">
      {forecast.map((day, i) => {
        const sev = day.severity ?? 0
        const c   = SEV_COLORS[Math.min(sev, 5)] ?? SEV_COLORS[0]
        const isToday   = i === 0
        const isTrigger = triggerDate && day.date === triggerDate
        const dateLabel = new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })
        const emoji     = WEATHER_EMOJI[day.category] ?? '🌡️'

        return (
          <div
            key={day.date}
            className={`
              flex-shrink-0 flex flex-col items-center gap-1.5 rounded-xl px-3 py-3 border transition-all
              ${c.bg} ${c.border}
              ${isToday ? 'ring-2 ring-inset ring-cyan-500/40' : ''}
              ${isTrigger ? 'ring-2 ring-inset ring-red-500/50' : ''}
            `}
            style={{ minWidth: 72 }}
          >
            <div className="text-[10px] font-bold text-slate-400 text-center leading-tight">
              {isToday ? (
                <span className="text-cyan-400">Today</span>
              ) : (
                dateLabel.split('/').slice(0, 2).join('/').replace(/^(\w+)/, (_, d) => `${d}`)
              )}
              {isTrigger && !isToday && (
                <div className="text-red-400 font-black text-[9px]">▲ TRIGGER</div>
              )}
            </div>
            <div className="text-2xl leading-none">{emoji}</div>
            <div className={`text-xs font-black ${c.text}`}>{sev}/5</div>
            {day.max_temp_c != null && (
              <div className="text-[10px] text-slate-500">{formatTempF(day.max_temp_c)}</div>
            )}
            {day.precip_mm > 0 && (
              <div className="text-[9px] text-blue-400">{formatPrecipIn(day.precip_mm)}</div>
            )}
            {day.snowfall_cm > 0 && (
              <div className="text-[9px] text-purple-300">{formatSnowIn(day.snowfall_cm)} ❄️</div>
            )}
            <div className="w-full h-1 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(sev / 5) * 100}%`, background: c.bar }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}


// ── Actual P&L input (Done mode) ─────────────────────────────────────────────

function ActualPnLInput({ actionId, initialValue }) {
  const mutation = useSetActualValue()
  const [draft, setDraft] = useState(initialValue == null ? '' : String(initialValue))

  useEffect(() => {
    setDraft(initialValue == null ? '' : String(initialValue))
  }, [initialValue, actionId])

  const commit = () => {
    if (draft === '' || draft === String(initialValue)) return
    const n = Number(draft)
    if (!Number.isFinite(n)) return
    mutation.mutate({ actionId, valueUsd: n })
  }

  return (
    <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-violet-300 mb-1.5">
        Actual P&amp;L (manager-entered, negative allowed)
      </div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
          <input
            type="number"
            step="0.01"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
              if (e.key === 'Escape') {
                e.preventDefault()
                setDraft(initialValue == null ? '' : String(initialValue))
                e.currentTarget.blur()
              }
            }}
            placeholder="0.00"
            className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-violet-500/50 focus:bg-white/10 transition-colors"
          />
        </div>
        <span className="text-xs text-slate-500">
          {mutation.isPending ? 'saving…' : initialValue != null ? 'saved' : 'not set'}
        </span>
      </div>
    </div>
  )
}


// ── Comment input (Done mode footer) ─────────────────────────────────────────

function CommentInput({ actionId }) {
  const [draft, setDraft] = useState('')
  const addComment = useAddComment()

  const submit = () => {
    const text = draft.trim()
    if (!text) return
    addComment.mutate({ actionId, text }, { onSuccess: () => setDraft('') })
  }

  return (
    <div className="flex items-start gap-2 w-full">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            submit()
          }
        }}
        rows={1}
        placeholder="Add a comment… (⌘/Ctrl+Enter to post)"
        className="flex-1 min-w-0 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 focus:bg-white/10 transition-colors resize-none"
      />
      <button
        onClick={submit}
        disabled={!draft.trim() || addComment.isPending}
        className="px-3 py-2 rounded-lg text-sm font-medium bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Post
      </button>
    </div>
  )
}


// ── Main dialog ──────────────────────────────────────────────────────────────

/**
 * Props:
 *   action     — engine-shape action dict (id, category, action_text, target,
 *                pillar, urgency, urgency_reason, event_at, trigger_date,
 *                trigger_severity, pnl_*, justification, etc.)
 *   onClose    — close handler
 *   mode       — 'active' (default) | 'done'
 *   onAccept   — (active mode only) callback (actionId) => void
 *   onDismiss  — (active mode only) callback (actionId) => void
 *   doneMeta   — (done mode only) {
 *                  outcome: 'marked_done' | 'marked_dismissed',
 *                  actualValueUsd: number | null,
 *                  markedDoneAt: ISO string,
 *                  markedDoneByEmail: string,
 *                  markedDoneByName: string,
 *                }
 */
export default function ActionDetailDialog({
  action, onClose,
  mode = 'active',
  onAccept, onDismiss,
  doneMeta,
}) {
  const navigate   = useNavigate()
  const meta       = CATEGORY_META[action.category] ?? {}
  const Icon       = meta.icon ?? AlertTriangle
  const isDone     = mode === 'done'

  const [briefing, setBriefing]               = useState(null)
  const [briefingLoading, setBriefingLoading] = useState(true)
  const lastBriefingTrackedRef = useRef(null)

  // Mutations used only in Done mode.
  const undone = useMarkUndone()

  useEffect(() => {
    // The AI Manager Briefing is decision-support copy; it's not useful
    // retrospectively, so we hide the section AND skip the network call
    // entirely in done mode.
    if (isDone) {
      setBriefing(null)
      setBriefingLoading(false)
      return
    }
    setBriefing(null)
    setBriefingLoading(true)
    client.get(`/actions/${action.id}/briefing`)
      .then(({ data }) => setBriefing(data.briefing))
      .catch(() => setBriefing(null))
      .finally(() => setBriefingLoading(false))
  }, [action.id, isDone])

  useEffect(() => {
    if (!briefing || isDone) return
    const key = `${action.id}`
    if (lastBriefingTrackedRef.current === key) return
    lastBriefingTrackedRef.current = key
    trackEvent('Briefing Viewed', {
      surface: 'action_card',
      node_type: action.node_type,
      briefing_length: briefing.length,
    })
  }, [briefing, action.id, action.node_type, isDone])

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const canNavigate = action.store_id ||
    ['DC_YARD_CLEARANCE', 'INBOUND_RISK', 'LANE_RISK'].includes(action.category)

  const handleNavigate = () => {
    onClose()
    if (action.store_id) navigate(`/stores/${action.store_id}`)
    else navigate(`/allocation?dc=${action.dc}`)
  }

  // Financial Impact cards. In done mode, the manager-entered Actual is
  // appended as the first card so the Expected vs Actual contrast is the
  // first thing the eye lands on.
  const financials = [
    isDone && {
      label: 'Actual P&L',
      value: doneMeta?.actualValueUsd != null ? Number(doneMeta.actualValueUsd) : null,
      color: 'violet',
    },
    { label: action.pnl_label ?? 'Est. Savings', value: action.pnl_estimate, color: 'emerald' },
    action.margin_risk     > 0 && { label: 'Margin Risk',      value: action.margin_risk,     color: 'amber' },
    action.capital_at_risk > 0 && { label: 'Capital at Risk',  value: action.capital_at_risk, color: 'orange' },
    action.labor_cost      > 0 && { label: 'Labor Exposure',   value: action.labor_cost,      color: 'cyan' },
  ].filter(Boolean)

  const dialogNow = useNow(10 * 60 * 1000)
  const dialogLeadHours = leadTimeHours(action.event_at, dialogNow)

  const signals = [
    { label: 'Distribution Center', value: action.dc ? `${action.dc} — ${action.target?.split('—')[0]?.trim() || ''}` : null },
    { label: 'Trigger Date',        value: action.trigger_date },
    { label: 'Lead Time',           value: action.event_at ? formatLeadTime(dialogLeadHours) : null },
    { label: 'Weather Severity',    value: action.trigger_severity != null ? `${action.trigger_severity} / 5` : null },
    { label: 'Weeks on Hand',       value: action.wos != null ? `${action.wos.toFixed(1)} wks` : null },
    { label: 'Sensitivity Score',   value: action.sensitivity_score != null ? `${(action.sensitivity_score * 100).toFixed(1)}%` : null },
    { label: 'Node Type',           value: action.node_type },
  ].filter((r) => r.value)

  const colorMap = {
    emerald: 'text-emerald-400 bg-emerald-500/8 border-emerald-500/20',
    amber:   'text-amber-400 bg-amber-500/8 border-amber-500/20',
    orange:  'text-orange-400 bg-orange-500/8 border-orange-500/20',
    cyan:    'text-cyan-400 bg-cyan-500/8 border-cyan-500/20',
    violet:  'text-violet-400 bg-violet-500/8 border-violet-500/20',
  }

  const outcomeBadge = isDone && doneMeta?.outcome ? OUTCOME_BADGE[doneMeta.outcome] : null
  const OutcomeIcon = outcomeBadge?.icon

  const dialog = (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ backdropFilter: 'blur(12px)', background: 'rgba(2,5,16,0.88)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 32 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 32 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="w-full sm:max-w-4xl max-h-[96vh] sm:max-h-[88vh] flex flex-col rounded-t-3xl sm:rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, rgba(10,16,40,0.99) 0%, rgba(6,10,28,0.99) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,212,255,0.06)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="relative flex-shrink-0 px-6 pt-6 pb-5"
          style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 60%)' }}
        >
          <div className={`absolute top-0 left-0 w-48 h-28 rounded-br-full opacity-20 blur-2xl pointer-events-none ${meta.bg ?? ''}`} />

          <div className="relative flex items-start gap-4">
            <div className={`flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center border-2 ${meta.bg ?? 'bg-white/5'} ${meta.border ?? 'border-white/10'}`}>
              <Icon size={22} className={meta.color ?? 'text-slate-300'} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3 flex-wrap">
                <div>
                  <div className={`text-[11px] font-bold uppercase tracking-[0.15em] ${meta.color ?? 'text-slate-400'} mb-0.5`}>
                    {CATEGORY_META[action.category]?.label ?? action.category}
                  </div>
                  <h2 className="text-xl font-black text-white leading-tight">
                    {action.action_text}
                  </h2>
                </div>
              </div>
              <div className="text-slate-400 text-sm mt-1 flex items-center gap-1.5">
                <span>{action.target}</span>
              </div>
              <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                {outcomeBadge && OutcomeIcon && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${outcomeBadge.color} ${outcomeBadge.bg} ${outcomeBadge.border}`}>
                    <OutcomeIcon size={10} />
                    {outcomeBadge.label}
                  </span>
                )}
                <PillarBadge pillar={action.pillar} />
                <UrgencyBadge
                  urgency={action.urgency}
                  reason={action.urgency_reason}
                  wos={action.wos}
                  leadTimeHours={dialogLeadHours}
                />
                {action.node_type && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-white/5 border border-white/10 text-slate-400">
                    <Layers size={9} />{action.node_type}
                  </span>
                )}
                {action.promo_multiplier > 1 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/25">
                    {action.promo_multiplier === 2.5 ? '🔥 BF/Cyber' : '⚡ Promo'} {action.promo_multiplier}×
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 rounded-xl text-slate-500 hover:text-white hover:bg-white/8 transition-all"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Scrollable body — identical across both modes ── */}
        <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-6" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>

          {/* 7-Day Weather Forecast — only useful for an active decision. */}
          {!isDone && (
            <section>
              <SectionLabel icon={Zap}>7-Day DC Forecast — {action.dc}</SectionLabel>
              <WeatherStrip dcId={action.dc} triggerDate={action.trigger_date} />
            </section>
          )}

          {/* Action Required + Reasoning */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <section className="flex flex-col">
              <SectionLabel icon={Activity}>Action Required</SectionLabel>
              <div className={`rounded-xl p-4 border flex-1 ${meta.bg ?? 'bg-white/5'} ${meta.border ?? 'border-white/10'}`}>
                <div className={`text-base font-black uppercase tracking-wide ${meta.color ?? 'text-slate-200'} mb-2`}>
                  {action.action_text}
                </div>
                {ACTION_TIPS[action.action_text] ? (
                  <p className="text-sm text-slate-300 leading-relaxed">{ACTION_TIPS[action.action_text]}</p>
                ) : (
                  <p className="text-sm text-slate-500 italic">No playbook tip available for this action type.</p>
                )}
              </div>
            </section>

            <section className="flex flex-col">
              <SectionLabel icon={Info}>Why This Action</SectionLabel>
              <div className="rounded-xl bg-white/[0.025] border border-white/5 p-4 flex-1">
                <p className="text-sm text-slate-300 leading-relaxed">{action.justification}</p>
                {action.promo_context && (
                  <div className="mt-3 flex items-start gap-2 pt-3 border-t border-white/5">
                    <Tag size={12} className="text-rose-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[10px] font-bold text-rose-400 uppercase tracking-wide mb-0.5">Active Promotion</div>
                      <p className="text-xs text-slate-400">{action.promo_context}</p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Financial Impact */}
          {financials.length > 0 && (
            <section className="mt-6">
              <SectionLabel icon={DollarSign}>Financial Impact</SectionLabel>
              <div className={`grid gap-3 ${financials.length === 1 ? 'grid-cols-1 max-w-xs' : financials.length === 2 ? 'grid-cols-2' : financials.length === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
                {financials.map(({ label, value, color }) => (
                  <div key={label} className={`rounded-xl border p-4 text-center ${colorMap[color]}`}>
                    <div className={`text-2xl font-black ${colorMap[color].split(' ')[0]}`}>{fmt(value)}</div>
                    <div className="text-[11px] text-slate-500 mt-1 leading-tight">{label}</div>
                  </div>
                ))}
              </div>
              {action.pnl_formula && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-white/[0.025] border border-white/5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-0.5 flex-shrink-0">Formula</span>
                  <span className="text-[12px] text-slate-400 font-mono leading-snug">{action.pnl_formula}</span>
                </div>
              )}
              {isDone && (
                <div className="mt-3">
                  <ActualPnLInput
                    actionId={action.id}
                    initialValue={doneMeta?.actualValueUsd}
                  />
                </div>
              )}
            </section>
          )}

          {/* Signal Data */}
          {signals.length > 0 && (
            <section>
              <SectionLabel icon={Layers}>Signal Data</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {signals.map(({ label, value }) => (
                  <div key={label} className="rounded-xl bg-white/[0.025] border border-white/5 px-4 py-3">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{label}</div>
                    <div className="text-sm font-bold text-slate-200">{value}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* AI Manager Briefing — decision-support copy, hidden in done mode. */}
          {!isDone && (
            <section className="pb-2">
              <SectionLabel icon={Bot}>AI Manager Briefing</SectionLabel>
              <div className="rounded-xl border border-cyan-500/20 p-5"
                style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.04) 0%, rgba(6,182,212,0.02) 100%)' }}
              >
                {briefingLoading ? (
                  <div className="flex items-center gap-2.5 text-sm text-slate-400">
                    <RefreshCw size={13} className="animate-spin text-cyan-400 flex-shrink-0" />
                    Generating manager briefing…
                  </div>
                ) : briefing ? (
                  <p className="text-sm text-slate-300 leading-relaxed">{briefing}</p>
                ) : (
                  <p className="text-sm text-slate-500 italic">Briefing unavailable — Ollama is offline or unreachable.</p>
                )}
              </div>
            </section>
          )}

          {/* Activity timeline (with comment input in Done mode).
              Sourced from /api/actions/{id}/history — available to all users
              (customer responses are sanitised server-side), unlike the
              cerebi-only /api/audit/* that powers the global Audit page. */}
          <section className="pb-2">
            <SectionLabel icon={Clock}>Activity</SectionLabel>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              {isDone && (
                <div className="mb-3 pb-3 border-b border-white/5">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                    <MessageSquare size={11} />
                    Add comment
                  </div>
                  <CommentInput actionId={action.id} />
                </div>
              )}
              <ActionHistoryTimeline actionId={action.id} showHeader={false} />
            </div>
          </section>
        </div>

        {/* ── Footer — mode-specific ── */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-white/5 flex items-center gap-3 flex-wrap"
          style={{ background: 'rgba(255,255,255,0.015)' }}
        >
          {isDone ? (
            <>
              <button
                onClick={() => undone.mutate(action.id, { onSuccess: onClose })}
                disabled={undone.isPending}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40 text-sm font-semibold transition-all"
              >
                <RotateCcw size={15} />
                {undone.isPending ? 'Reopening…' : 'Reopen action'}
              </button>
              <div className="text-xs text-slate-500 mp-mask truncate flex items-center gap-1.5">
                <UserIcon size={11} className="text-slate-600" />
                <span>
                  {outcomeBadge?.label || 'Acted'} by{' '}
                  <span className="text-slate-300">
                    {doneMeta?.markedDoneByName || doneMeta?.markedDoneByEmail || '—'}
                  </span>
                  {doneMeta?.markedDoneAt && (
                    <> · {fmtRelativeTs(doneMeta.markedDoneAt)}</>
                  )}
                </span>
              </div>
              {canNavigate && (
                <button
                  onClick={handleNavigate}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 text-sm font-semibold transition-all ml-auto"
                >
                  <ExternalLink size={15} />
                  {action.store_id ? 'View Store Detail' : 'View Allocation'}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => { onAccept?.(action.id); onClose() }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 text-sm font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/10"
              >
                <CheckCircle2 size={15} />
                Accept Action
              </button>
              <button
                onClick={() => { onDismiss?.(action.id); onClose() }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 text-sm font-semibold transition-all"
              >
                <XCircle size={15} />
                Dismiss
              </button>
              {canNavigate && (
                <button
                  onClick={handleNavigate}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 text-sm font-semibold transition-all ml-auto"
                >
                  <ExternalLink size={15} />
                  {action.store_id ? 'View Store Detail' : 'View Allocation'}
                </button>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  )

  return createPortal(dialog, document.body)
}
